// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Icon, Space, Styled, Popover, DateInput} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  useDebouncedCallback,
  useEffect,
  useResponsive,
  usePopover,
  useState,
  useForm,
  ResponsiveType,
  UrlFiltersType,
  Form,
  PopoverType,
} from '@supermove/hooks';
import {Organization, UserModel, ReferralSource, OrganizationModel} from '@supermove/models';
import {Environment} from '@supermove/sdk';
import {Datetime} from '@supermove/utils';

// App
import SecondaryButton from '@shared/design/components/Button/SecondaryButton';
import ContextSwitcher from '@shared/design/components/ContextSwitcher';
import FieldInput from '@shared/design/components/Field/FieldInput';
import MultiDropdownCheckboxInput from '@shared/design/components/Field/MultiDropdownCheckboxInput';
import SearchBar from '@shared/design/components/SearchBar';
import PopoverFilterWithFooter from '@shared/modules/App/components/PopoverFilterWithFooter';
import {
  SalesPageContentTabs,
  SalesPageContentTabsType,
} from '@shared/modules/Sales/components/SalesPageContent';
import UserRole from '@shared/modules/User/enums/UserRole';

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const Container = Styled.View`
`;

const RowFilterInputContainer = Styled.View`
  flex: 1;
`;

const Section = Styled.View<{index: number}>`
  z-index: ${({index}) => 100 - index};
`;

const FiltersContainer = Styled.View<{responsive: ResponsiveType}>`
  padding-horizontal: ${({responsive}) => (responsive.desktop ? 24 : 16)}px;
`;

type LeadFiltersType = {
  moveStartDate: string;
  moveEndDate: string;
  salespersonIds: string[];
  coordinatorIds: string[];
  projectTypeIds: string[];
  projectTagIds: string[];
  crewOrganizationIds: string[];
  referralSources: string[];
};
type FiltersFormType = Form<LeadFiltersType>;

export const SALES_LEADS_FILTERS_KEYS: (keyof LeadFiltersType)[] = [
  'moveStartDate',
  'moveEndDate',
  'salespersonIds',
  'coordinatorIds',
  'projectTypeIds',
  'projectTagIds',
  'crewOrganizationIds',
  'referralSources',
];

export type SalesLeadsFiltersType = {
  view: string;
  page: number;
  searchQuery: string;
} & LeadFiltersType;
type SalesLeadsUrlFiltersType = UrlFiltersType<SalesLeadsFiltersType>;

const handleApply = ({
  urlFilters,
  form,
  leadsListFiltersPopover,
}: {
  urlFilters: SalesLeadsUrlFiltersType;
  form: FiltersFormType;
  leadsListFiltersPopover: PopoverType;
}) => {
  const {moveStartDate, moveEndDate, ...filters} = form.values;
  const preparedFilters = Object.entries(filters).reduce((result, [key, value]) => {
    if (_.isEmpty(value)) {
      return {...result, [key]: undefined};
    }
    return {...result, [key]: value};
  }, {});
  urlFilters.handleUpdate({
    ...preparedFilters,
    moveStartDate: moveStartDate ? Datetime.convertToDate(form.values.moveStartDate) : undefined,
    moveEndDate: moveEndDate ? Datetime.convertToDate(form.values.moveEndDate) : undefined,
    page: 1,
  });
  leadsListFiltersPopover.handleClose();
};

const RESET_FILTERS = {
  moveStartDate: '',
  moveEndDate: '',
  salespersonIds: [],
  coordinatorIds: [],
  projectTypeIds: [],
  projectTagIds: [],
  crewOrganizationIds: [],
  referralSources: [],
} as LeadFiltersType;

const DateFilter = ({
  index,
  dateKey,
  label = ' ',
  position,
  form,
}: {
  index: number;
  dateKey: keyof LeadFiltersType;
  label?: string;
  position: string;
  form: FiltersFormType;
}) => {
  const dateValue = form.values[dateKey];
  return (
    <RowFilterInputContainer>
      <FieldInput
        {...form}
        index={index}
        name={dateKey}
        label={label}
        component={DateInput}
        input={{
          placeholder: 'MM/DD/YYYY',
          setFieldValue: form.setFieldValue,
          position,
          style: {width: '100%'},
          value: dateValue ? Datetime.toFormDate(dateValue) : undefined,
        }}
      />
    </RowFilterInputContainer>
  );
};

const StaffDropdownInput = ({
  viewer,
  index,
  form,
  field,
  label,
  placeholder,
}: {
  viewer?: UserModel;
  index: number;
  form: FiltersFormType;
  field: keyof LeadFiltersType;
  label: string;
  placeholder: string;
}) => {
  const teamMemberDropdownOptions = viewer?.viewingOrganization
    ? Organization.getAllOfficeStaffOptions(viewer.viewingOrganization).map((staff) => ({
        ...staff,
        value: _.toString(staff.value),
      }))
    : [];

  // Prepend the option "None" to the dropdown
  teamMemberDropdownOptions.unshift({
    // @ts-expect-error Needs updated types in MultiDropdownCheckboxInput
    value: null,
    label: 'None',
    description: 'Show leads without an assigned salesperson',
  });

  return (
    <FieldInput
      {...form}
      index={index}
      name={field}
      label={label}
      component={MultiDropdownCheckboxInput}
      input={{
        setFieldValue: form.setFieldValue,
        options: teamMemberDropdownOptions,
        placeholder,
        style: {flex: 1},
        isSearchable: true,
        isEnabledSelectAll: true,
        isDescriptionBelow: true,
        isResponsive: true,
        usePills: true,
      }}
    />
  );
};

const ProjectTypeDropdownInput = ({
  index,
  form,
  viewer,
}: {
  index: number;
  form: FiltersFormType;
  viewer: UserModel;
}) => {
  return (
    <FieldInput
      {...form}
      index={index}
      component={MultiDropdownCheckboxInput}
      name={'projectTypeIds'}
      label={'Project Types'}
      input={{
        options: viewer.viewingOrganization.projectTypes.map((projectType) => ({
          label: projectType.name,
          value: projectType.id,
        })),
        placeholder: 'Select project types',
        setFieldValue: form.setFieldValue,
        style: {flex: 1},
        usePills: true,
        isResponsive: true,
      }}
    />
  );
};

const LaborSourceMultiselect = ({
  index,
  form,
  viewer,
}: {
  index: number;
  form: FiltersFormType;
  viewer: UserModel;
}) => {
  const getLaborSourceOptions = ({organizations}: {organizations: OrganizationModel[]}) => {
    const sortedOrganizationsByName = _.sortBy(organizations, ['name']);
    const laborSourceOptions = sortedOrganizationsByName.map((organization) => ({
      label: organization.name,
      value: organization.id,
    }));
    return [
      {
        label: 'No Labor Source',
        value: 'NO_CREW',
      },
      ...laborSourceOptions,
    ];
  };
  return (
    <FieldInput
      {...form}
      index={index}
      component={MultiDropdownCheckboxInput}
      name={'crewOrganizationIds'}
      label={'Labor Sources'}
      isResponsive
      input={{
        options: getLaborSourceOptions({
          organizations: viewer.viewingOrganization.company.organizations,
        }),
        placeholder: 'Select labor sources',
        setFieldValue: form.setFieldValue,
        style: {flex: 1},
        usePills: true,
        isResponsive: true,
      }}
    />
  );
};

const ReferralSourceMultiselect = ({
  index,
  name,
  form,
  viewer,
}: {
  index: number;
  name: string;
  form: FiltersFormType;
  viewer: UserModel;
}) => {
  const {referralSources} = viewer.viewingOrganization.settings;
  return (
    <FieldInput
      {...form}
      component={MultiDropdownCheckboxInput}
      name={'referralSources'}
      isResponsive
      label={'Referral Sources'}
      style={{
        zIndex: 100 - index,
      }}
      input={{
        options: ReferralSource.getDropdownOptions({referralSources}),
        placeholder: 'Select referral sources',
        setFieldValue: form.setFieldValue,
        style: {flex: 1},
        usePills: true,
        isResponsive: true,
      }}
    />
  );
};

const LeadListFilterFields = ({
  viewer,
  form,
  tab,
}: {
  viewer: UserModel;
  form: FiltersFormType;
  tab: SalesPageContentTabsType;
}) => {
  const teamMemberDropdownOptions = viewer.viewingOrganization
    ? Organization.getAllOfficeStaffOptions(viewer.viewingOrganization).map((staff) => ({
        ...staff,
        value: _.toString(staff.value),
      }))
    : [];

  // Prepend the option "None" to the dropdown
  teamMemberDropdownOptions.unshift({
    // @ts-expect-error Needs updated types in MultiDropdownCheckboxInput
    value: null,
    label: 'None',
    description: 'Show leads without an assigned salesperson',
  });

  const role = Environment.isSalesApp() ? viewer.salesAppRole : viewer.role;

  return (
    <React.Fragment>
      <Section index={0}>
        <Row>
          <DateFilter
            index={1}
            dateKey={'moveStartDate'}
            label={'Move Date Range'}
            position={DateInput.Positions.BOTTOM_START}
            form={form}
          />
          <Space width={16} />
          <DateFilter
            index={2}
            dateKey={'moveEndDate'}
            position={DateInput.Positions.BOTTOM_END}
            form={form}
          />
        </Row>
      </Section>
      <Space height={16} />
      <ProjectTypeDropdownInput index={1} form={form} viewer={viewer} />
      {tab === SalesPageContentTabs.COMPANY_LEADS && (
        <React.Fragment>
          <Space height={16} />
          {UserRole.ADMIN_ROLES_PLUS_SUPER.includes(role) && (
            <StaffDropdownInput
              viewer={viewer}
              index={4}
              form={form}
              field={'salespersonIds'}
              label={'Salesperson'}
              placeholder={'Select salespersons'}
            />
          )}
        </React.Fragment>
      )}
      <Space height={16} />
      <StaffDropdownInput
        viewer={viewer}
        index={5}
        form={form}
        field={'coordinatorIds'}
        label={'Coordinator'}
        placeholder={'Select coordinators'}
      />
      <Space height={16} />
      <LaborSourceMultiselect index={6} form={form} viewer={viewer} />
      <Space height={16} />
      <ReferralSourceMultiselect index={7} name={'referralSources'} form={form} viewer={viewer} />
    </React.Fragment>
  );
};

const LeadListFiltersContent = ({
  viewer,
  tab,
  urlFilters,
  leadsListFiltersPopover,
}: {
  viewer?: UserModel;
  tab: SalesPageContentTabsType;
  urlFilters: SalesLeadsUrlFiltersType;
  leadsListFiltersPopover: PopoverType;
}) => {
  const responsive = useResponsive();
  const {salespersonIds, coordinatorIds} = urlFilters.getFilters();
  const form = useForm<LeadFiltersType>({
    initialValues: {
      ...urlFilters.getFilters(),
      salespersonIds: salespersonIds || [],
      coordinatorIds: coordinatorIds || [],
    },
  });

  return (
    <Container responsive={responsive}>
      <Popover.Content innerRef={leadsListFiltersPopover.ref}>
        <SecondaryButton
          isResponsive
          text={urlFilters.getFilterCountLabel({
            filterKeys: SALES_LEADS_FILTERS_KEYS,
            isResponsive: true,
          })}
          iconLeft={Icon.Filter}
          onPress={leadsListFiltersPopover.handleToggle}
          isWidthOfContainer
        />
      </Popover.Content>
      <PopoverFilterWithFooter
        activeFiltersCount={urlFilters.getFilterCount({
          filterKeys: SALES_LEADS_FILTERS_KEYS,
        })}
        handleClear={() => form.setValues(RESET_FILTERS)}
        handleApply={() =>
          handleApply({
            urlFilters,
            form,
            leadsListFiltersPopover,
          })
        }
        popover={leadsListFiltersPopover}
      >
        {viewer && <LeadListFilterFields viewer={viewer} form={form} tab={tab} />}
        <Space height={16} />
      </PopoverFilterWithFooter>
    </Container>
  );
};

const SalesLeadsFilters = ({
  viewer,
  tab,
  urlFilters,
}: {
  viewer?: UserModel;
  tab: SalesPageContentTabsType;
  urlFilters: SalesLeadsUrlFiltersType;
}) => {
  const responsive = useResponsive();
  const [searchValue, setSearchValue] = useState(urlFilters.get('searchQuery') || '');
  const leadsListFiltersPopover = usePopover({name: 'Lead List Filters Popover'});

  // Reset input value if a top level navigation resets the searchQuery
  useEffect(() => {
    if (!urlFilters.get('searchQuery')) {
      setSearchValue('');
    }
  }, [urlFilters.get('searchQuery')]);

  const handleChangeText = useDebouncedCallback(
    () =>
      urlFilters.handleUpdate({
        searchQuery: searchValue,
        page: 1,
      }),
    750,
  );

  const handleInputChange = (text: string) => {
    setSearchValue(text);
    handleChangeText();
  };

  const isBoardView = urlFilters.get('view') === 'BOARD';

  return (
    <React.Fragment>
      <FiltersContainer responsive={responsive}>
        <Row>
          <SearchBar
            placeholder={
              responsive.desktop ? 'Search for project identifier or contact information' : 'Search'
            }
            onChangeText={handleInputChange}
            containerStyle={{flex: 1}}
            style={{flex: 1}}
            isResponsive
            valueOverride={searchValue}
          />
          <Space width={16} />
          <LeadListFiltersContent
            key={leadsListFiltersPopover.key}
            viewer={viewer}
            urlFilters={urlFilters}
            tab={tab}
            leadsListFiltersPopover={leadsListFiltersPopover}
          />
          {responsive.desktop && viewer?.viewingOrganization.features.isEnabledLeadsBoardView && (
            <React.Fragment>
              <Space width={16} />
              <ContextSwitcher
                contextDefinitions={[
                  {
                    iconSource: Icon.ChartKanban,
                    isSelected: isBoardView,
                    onPress: () => urlFilters.handleUpdate({view: 'BOARD'}),
                  },
                  {
                    iconSource: Icon.Bars,
                    isSelected: !isBoardView,
                    onPress: () => urlFilters.handleUpdate({view: undefined}),
                  },
                ]}
              />
            </React.Fragment>
          )}
        </Row>
      </FiltersContainer>
    </React.Fragment>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
SalesLeadsFilters.fragment = gql`
  ${Organization.getAllOfficeStaffOptions.fragment}
  ${ReferralSource.getDropdownOptions.fragment}

  fragment SalesLeadsFilters on User {
    id
    role
    salesAppRole
    viewingOrganization {
      id
      features {
        isEnabledLeadsBoardView: isEnabled(feature: "LEADS_BOARD_VIEW")
      }
      projectTypes: projectTypesForFilters(category: "MOVE") {
        id
        name
      }
      company {
        id
        organizations {
          id
          name
        }
      }
      settings {
        id
        referralSources {
          name
          ...ReferralSource_getDropdownOptions
        }
      }
      ...Organization_getAllOfficeStaffOptions
    }
  }
`;

export default SalesLeadsFilters;
